import { Recurrence } from '@tableau/self-service-schedules';
import { ButtonType, Chip, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, DialogWidget, ListItem, ListItemFilterFn, LocallyFilteringList, TabContent, TabListAlignment, TabsWidget, TextFieldWidget, ToastType, ToastWidget } from '@tableau/widgets';
import React from 'react';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { editDatasource } from '~src/datasource/actions/EditDatasourceAction';
import { createEditDataSourceButton } from '~src/datasource/components/DatasourceSpaceHeaderButtons';
import { Flows } from '~src/flow/resources/Flows';
import { GeocodingTaskInfo } from '~src/geocoding/models/GeocodingTaskInfo';
import { AddUpdateGeocodingTaskRequestConfigParams, callAddUpdateGeocodingTaskViaApiService, callGetTaskViaApiService, callQueueEstimationJobViaApiService, callRunGeocodingTaskViaApiService, GeocodingJob, GeocodingJobResponse, GeocodingTask, GeocodingTaskResponse } from '~src/geocoding/requests/GeocodingBackgrounderRequests';
import { callAddUpdateConfigViaApiService, callGetConfigViaApiService, GeocodingConfig, GeocodingConfigResponse } from '~src/geocoding/requests/GeocodingConfigRequests';
import { callGetColumnsViaApiService, GetDatasourceColumnsResponse } from '~src/geocoding/requests/GetDatasourceColumns';
import { GeocodingResponseHelper } from '~src/geocoding/utils/GeocodingResponseHelper';
import { SelfServiceScheduleUtils } from '~src/schedule/utils/SelfServiceScheduleUtils';
import { AllActionsAllowed } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { ReactModalService } from '../../../commonInfra/dialogs/ReactModalService';
import { EstimateTab } from './EstimateTab';
import { GeocodeTab } from './GeocodeTab';

export interface GeocodingConfigDialogProps {
  datasourceName: string;
  datasourceLuid: string;
  datasourceId: string;
  datasource: IContentInfo<'datasource'>;
  geocodingConfig?: GeocodingConfig;
  geocodingTask?: GeocodingTask;
  columns: ListItem[];
  colNameToCaptionMap: Record<string, string>;
  close: () => void;
  updateRecord: () => void;
}

/**
 * Options used for ReactModalService
 */
export function launchGeocodingConfigDialog(props: Omit<GeocodingConfigDialogProps, 'close'>): void {
  ReactModalService.openCustom(closeAndDismiss => {
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.NormalWithTitleBar,
      title: Messages.geocodingDialogTitle({DS_NAME: props.datasourceName}),
      dialogWidth: TabDialogWidth.width700px,
      shouldNotCloseOnGlassClick: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      showCloseButton: true,
      disableAutoFocus: true,
      testId: 'geocoding-config-dialog'
    }, closeAndDismiss);

    return (
      <DialogWidget {...dialogProps}>
        <GeocodingConfigDialog
          datasourceName={props.datasourceName}
          datasourceLuid={props.datasourceLuid}
          datasourceId={props.datasourceId}
          geocodingConfig={props.geocodingConfig}
          geocodingTask={props.geocodingTask}
          columns={props.columns}
          colNameToCaptionMap={props.colNameToCaptionMap}
          close={closeAndDismiss.close}
          updateRecord={props.updateRecord}
          datasource={props.datasource}
        />
      </DialogWidget>
    );
  });
}

/**
 * For opening the dialog from the Tasks page
 */
export function fetchGeocodingConfigAndLaunchDialog(config: GeocodingTaskInfo, datasource: IContentInfo<'datasource'>) {
  // TODO: add error handling
  callGetConfigViaApiService({configLuid: config.geocodingConfigLuid})
  .then(
    (configResult: GeocodingConfigResponse) => {
      const taskLuid = configResult.geocodingConfig.runGeocodingTask;
      callGetTaskViaApiService({taskLuid})
      .then(
        (taskResult: GeocodingTaskResponse) => {
          const datasourceLuid = configResult.geocodingConfig.datasourceId;
          callGetColumnsViaApiService({datasourceLuid})
          .then(
            (columnsResult: GetDatasourceColumnsResponse) => {
              const { columns, colNameToCaptionMap } = GeocodingResponseHelper.processGetDatasourceColumnsResponse(columnsResult);
              launchGeocodingConfigDialog({
                datasourceName: datasource.name,
                datasourceLuid: datasourceLuid,
                datasourceId: datasource.id,
                datasource,
                geocodingConfig: GeocodingResponseHelper.processGeocodingConfigResponse(configResult.geocodingConfig),
                geocodingTask: taskResult.geocodingTask,
                columns,
                colNameToCaptionMap,
                updateRecord: () => {}
              });
              return Promise.resolve();
            },
            (reason: GetDatasourceColumnsResponse) => {
              return Promise.reject(reason);
            }
          );
        },
        (reason: GeocodingTaskResponse) => {
          return Promise.reject(reason);
        }
      );
    },
    (reason: GeocodingConfigResponse) => {
      return Promise.reject(reason);
    }
  );
}

interface ConfigDialogState {
  configName: string;
  configLuid?: string;
  selectedColumnNames: string[];
  isGeocodeTabActive: boolean;
  columns: ListItem[];
  tokenLimit: number;
  scheduleRecurrence: Recurrence;
  lastEstimationJob?: GeocodingJob;
  lastEstimation?: number;
}

export class GeocodingConfigDialog extends React.Component<GeocodingConfigDialogProps, ConfigDialogState> {
  private hasAssociatedFlows: boolean;

  public constructor(props: GeocodingConfigDialogProps) {
    super(props);
    this.state = {
      configName: this.props.geocodingConfig?.name ?? '',
      selectedColumnNames: this.props.geocodingConfig?.columnNames ?? [],
      isGeocodeTabActive: true,
      columns: props.columns,
      tokenLimit: this.props.geocodingConfig?.tokenLimit ?? 100,
      scheduleRecurrence: SelfServiceScheduleUtils.getRecurrenceFromSSSchedule(props.geocodingTask?.schedule),
      configLuid: this.props.geocodingConfig?.id,
      lastEstimation: this.props.geocodingConfig?.lastEstimation,
      lastEstimationJob: this.props.geocodingConfig?.lastEstimationJob
    };

    this.hasAssociatedFlows = this.getHasAssociatedFlows();
  }

  private getHasAssociatedFlows(): boolean {
    if (!SessionSiteSelectors.flowsOnSiteEnabled(getAppStore().getState()))
      return false;

    Flows.getFlowsByDatasourceId(this.props.datasourceLuid).then(
      (flows) => {
        Promise.resolve(flows);
        return flows.length > 0;
      },
      (reason) => {
        Promise.reject(reason);
        return false;
      }
    );
  }

  private itemFilterFn: ListItemFilterFn = (item: ListItem, filterValue: any) => item.tooltip.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;

  private buildColumnList(): React.ReactNode {
    return (
      <div className='geocodingConfigDialogColList'>
        <div>{Messages.geocodingConfigDialogListLabel()}</div>
        <LocallyFilteringList
          testId={'geocoding-columns'}
          items={this.state.columns}
          placeholder={Messages.geocodingConfigDialogSearch()}
          onSelect={(selected: string) => this.setState({ selectedColumnNames: [...this.state.selectedColumnNames, selected]})}
          selectedValues={new Set<string>(this.state.selectedColumnNames)}
          itemFilterFn={this.itemFilterFn}
        />
      </div>
    );
  }

  private buildFieldConfigArea(): React.ReactNode {
    const { selectedColumnNames } = this.state;
    if (selectedColumnNames.length === 0) {
      return <div className='geocodingFieldConfigArea'>{Messages.geocodingConfigDialogNoSelectionText()}</div>;
    }

    return (
      <div className='geocodingFieldConfigArea'>
        <div>
          <div className='geocodingChipLabel'>{Messages.geocodingConfigDialogChipLabel()}</div>
          {this.renderChips(selectedColumnNames)}
        </div>
        <div className='geocodingInfoTextBlock'>
          <div className='geocodingBoldText'>{Messages.geocodingConfigDialogInfoTitle()}</div>
          <ul>
            <li>{Messages.geocodingConfigDialogInfoTip1()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip2()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip3()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip4()}</li>
            <li>{Messages.geocodingConfigDialogInfoTip5()}</li>
          </ul>
        </div>
        <div className="geocodingEditorButton">
          {this.buildDatasourceEditorButton()}
        </div>
      </div>
    );
  }

  private buildConfigNameInput(): JSX.Element {
    return (<TextFieldWidget
      kind="outline"
      label={Messages.geocodingConfigDialogConfigNameLabel()}
      onChange={e => this.setState({ configName: e.target.value})}
      onClear={() => this.setState({ configName: ''})}
      placeholder={Messages.geocodingConfigDialogConfigNamePlaceholder()}
      testId='geocoding-config-name'
      style={{ width: '100%', marginBottom: '15px'}}
      value={this.state.configName}
    />);
  }

  private buildDatasourceEditorButton(): JSX.Element {
    const editDatasourceWrapper = () => {
      getAppStore().dispatch(editDatasource(this.props.datasource, false /* skipPrepCheck */));
    };
    return createEditDataSourceButton({
      item: this.props.datasource,
      actions: AllActionsAllowed,
      editDatasource: editDatasourceWrapper,
      hasAssociatedFlows: this.hasAssociatedFlows,
    });
  }

  private renderChips(colNames: string[]): JSX.Element {
    const chips = colNames.map((colName, index) => {
      const colCaption = this.props.colNameToCaptionMap[colName];
      const handleOnRemove = () => {
        let updatedColumns = [...this.state.selectedColumnNames];
        updatedColumns.splice(index, 1);
        this.setState({ selectedColumnNames: updatedColumns });
      };
      return (
        <Chip className='geocodingColumnChip'
              testId='geocoding-selected-column'
              key={`chip-${index}-${colName}`}
              tooltip={colCaption}
              value={colName}
              onRemove={handleOnRemove}>
          {colCaption}
        </Chip>
      );
    });
    return (<div>{chips}</div>);
  }

  private handleTabChanged = (index: number) => {
    if (index === 0) {
      this.setState({isGeocodeTabActive: false});
    } else {
      this.setState({isGeocodeTabActive: true});
    }
  };

  private buildTabs(): React.ReactNode {
    const { selectedColumnNames, tokenLimit } = this.state;
    const isScheduleChecked = !!this.props.geocodingTask?.schedule;
    return (
      <TabsWidget
        className='geocodingConfigTabs'
        ariaLabel=''
        initialActiveIndex={1}
        tabListAlignment={TabListAlignment.Left}
        onActiveIndexChanged={this.handleTabChanged}
        tabs={[
          {
            tabContent: { label: Messages.geocodingConfigEstimateTabLabel() } as TabContent,
            panelContent: <EstimateTab columnNames={selectedColumnNames} />,
          },
          {
            tabContent: { label: Messages.geocodingConfigGeocodeTabLabel() } as TabContent,
            panelContent: <GeocodeTab
                                      columnNames={selectedColumnNames}
                                      tokenLimit={tokenLimit}
                                      scheduleRecurrence={this.state.scheduleRecurrence}
                                      handleTokenLimitChange={(val) => this.setState({ tokenLimit: val })}
                                      handleScheduleChange={(recurrence) => this.setState({scheduleRecurrence: recurrence})}
                                      handleEstimateJob={() => this.handleEstimateJob()}
                                      lastEstimationJob={this.state.lastEstimationJob}
                                      lastEstimation={this.state.lastEstimation}
                                      isScheduleChecked={isScheduleChecked} />,
          },
        ]}
        testId='config-tabs'
      />
    );
  }

  public addGeocodingConfigs(datasourceLuid: string): void {
    callAddUpdateConfigViaApiService(
      datasourceLuid,
      {
        geocodingConfigName: this.state.configName,
        geocodingColumnNames: this.state.selectedColumnNames,
        geocodingTokenLimit: this.state.tokenLimit,
        geocodingConfigId: this.state.configLuid
      })
    .then(
      (response: GeocodingConfigResponse) => {
        this.addUpdateGeocodingTask(GeocodingResponseHelper.processGeocodingConfigResponse(response.geocodingConfig));
        return Promise.resolve(response);
      },
      (reason: GeocodingConfigResponse) => {
        return Promise.reject(reason);
      }
    );
  }

  private addUpdateGeocodingTask(config: GeocodingConfig) {
    if (!config.id) {
      this.props.updateRecord();
      return;
    }

    const request: AddUpdateGeocodingTaskRequestConfigParams = {
      schedule: SelfServiceScheduleUtils.getSSScheduleFromRecurrence(this.state.scheduleRecurrence),
      geocodingConfigId: config.id,
    };

    callAddUpdateGeocodingTaskViaApiService(request).then(
      (response: GeocodingTaskResponse) => {
        this.runTask(response.geocodingTask.id);
        Promise.resolve(response);
      },
      (reason: GeocodingTaskResponse) => {
        this.props.updateRecord();
        Promise.reject(reason);
      }
    );
  }

  private runTask(taskLuid: string) {
    if (!taskLuid) {
      this.props.updateRecord();
      return;
    }
    callRunGeocodingTaskViaApiService({taskLuid}).then(
      (response: GeocodingJobResponse) => {
        this.showRunGeocodingTaskAlert();
        this.props.updateRecord();
        Promise.resolve(response);
      },
      (reason: GeocodingJobResponse) => {
        this.props.updateRecord();
        Promise.reject(reason);
      }
    );
  }

  private showRunGeocodingTaskAlert() {
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    ToastWidget.show({
      testId: 'geocoding-task',
      toastType: ToastType.Success,
      message: Messages.geocodingConfigTaskSuccessToast(),
      actionHref: siteUrls.jobsUrl(),
      actionMessage: Messages.geocodingConfigTaskSuccessToastAction()
    });
  }

  private handleEstimateJob(): void {
    // TODO: Config must be saved in postgres in order for estimation to work. Add dialog confirming user wants to do so
    if (!this.state.configLuid) {
      // add config to postgres if it isn't already
      callAddUpdateConfigViaApiService(
        this.props.datasourceLuid,
        {
          geocodingConfigName: this.state.configName,
          geocodingColumnNames: this.state.selectedColumnNames,
          geocodingTokenLimit: this.state.tokenLimit,
          geocodingConfigId: this.state.configLuid
        }
      ).then(
        (response: GeocodingConfigResponse) => {
          this.handleQueueEstimationJob(response.geocodingConfig.id);
        },
        (reason: GeocodingConfigResponse) => {
          return Promise.reject(reason);
        }
      );
    } else {
      // if config exists, just queue job
      this.handleQueueEstimationJob(this.state.configLuid);
    }
  }

  private handleQueueEstimationJob(configLuid: string) {
    callQueueEstimationJobViaApiService({configLuid}).then(
      (response: GeocodingJobResponse) => {
        this.props.updateRecord();
        this.setState({lastEstimationJob: response.geocodingJob});
        this.showQueueEstimationJobAlert();
        return Promise.resolve(response);
      },
      (reason: GeocodingJobResponse) => {
        this.props.updateRecord();
        return Promise.reject(reason);
      }
    );
  }

  // TODO: move verbiage to Messages to be localized once design and text are finalized
  private showQueueEstimationJobAlert() {
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    ToastWidget.show({
      testId: 'geocoding-estimation',
      toastType: ToastType.Success,
      message: 'Geocoding estimation job queued.',
      actionHref: siteUrls.jobsUrl(),
      actionMessage: Messages.geocodingConfigTaskSuccessToastAction()
    });
  }

  private handleConfirmAction = () => {
    this.addGeocodingConfigs(this.props.datasourceLuid);
    this.props.close();
  };

  private renderFooterButtons(): JSX.Element {
    return (
      <DialogFooterButtonsWidget
        buttons={[
          {
            children: Messages.geocodingDialogCancelButton(),
            handleClick: this.props.close,
            testId: 'geocoding-cancel'
          }, {
            buttonType: ButtonType.Primary,
            children: this.state.isGeocodeTabActive
              ? Messages.geocodingConfigGeocodeButton()
              : Messages.geocodingConfigEstimateButton(),
            disabled: this.state.selectedColumnNames.length === 0,
            handleClick: this.handleConfirmAction,
            testId: this.state.isGeocodeTabActive ? 'geocoding-run' : 'geocoding-estimate'
          }
        ]}
      />
    );
  }

  public render(): React.ReactNode {
    return (
      <div className='geocodingConfigDialogContainer'>
        <div className='geocodingConfigDialogMainContent'>
          {this.buildColumnList()}
          <div className='geocodingConfigDialogContent'>
            <div className="geocodingConfigInputFieldsArea">
              {this.buildConfigNameInput()}
              {this.buildFieldConfigArea()}
            </div>
            {this.state.selectedColumnNames.length > 0 &&
              this.buildTabs()
            }
          </div>
        </div>
        {this.renderFooterButtons()}
      </div>
    );
  }
}
